import type { GameScene } from '../scenes/GameScene';
import { Direction } from '../data/world-config';

interface ServerMessage {
  type: string;
  playerId?: string;
  x?: number;
  y?: number;
  direction?: string | number;
  players?: Record<string, { x: number; y: number; direction: string }> | Array<{ playerId: string; x: number; y: number; direction: string; health?: number }>;
  room?: {
    roomId: string;
    players: Record<string, { playerId: string; x: number; y: number; direction: string }>;
    dayCyclePhase?: string;
    dayNumber?: number;
  };
  player?: { playerId: string; x: number; y: number; direction: string };
  playerCount?: number;
  text?: string;
  displayName?: string;
  message?: string;
  dayCyclePhase?: string;
  dayCycleTick?: number;
  dayNumber?: number;
  phase?: string;
  [key: string]: unknown;
}

const DIR_TO_STRING: Record<number, string> = {
  [Direction.DOWN]: 'down',
  [Direction.LEFT]: 'left',
  [Direction.RIGHT]: 'right',
  [Direction.UP]: 'up',
};

const STRING_TO_DIR: Record<string, Direction> = {
  'down': Direction.DOWN,
  'left': Direction.LEFT,
  'right': Direction.RIGHT,
  'up': Direction.UP,
};

export class NetworkManager {
  private scene: GameScene;
  private ws: WebSocket | null = null;
  private playerId: string = '';
  private lastSendTime: number = 0;
  private sendInterval: number = 50;
  private lastX: number = 0;
  private lastY: number = 0;
  private connected: boolean = false;
  private roomId: string = 'default';

  // Auto-reconnection
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalDisconnect: boolean = false;

  // Connection status
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting' = 'disconnected';

  constructor(scene: GameScene) {
    this.scene = scene;
    this.playerId = `player_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  }

  async connect(roomId: string = 'default'): Promise<void> {
    this.roomId = roomId;
    this.intentionalDisconnect = false;
    this.connectionState = 'connecting';
    this.scene.events.emit('connection-state', this.connectionState);

    // Create or ensure room exists via REST
    try {
      await this.ensureRoomExists(roomId);
    } catch {
      // Room may already exist, continue
    }

    this.openWebSocket();
  }

  private openWebSocket(): void {
    // Clean up old socket if any
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = location.host;

    try {
      this.ws = new WebSocket(`${protocol}//${host}/api/rooms/${this.roomId}/ws`);

      this.ws.onopen = () => {
        this.connected = true;
        this.reconnectAttempts = 0;
        this.connectionState = 'connected';
        this.scene.events.emit('connection-state', this.connectionState);
        this.scene.events.emit('notification', 'Connected to multiplayer!');

        // Send join message
        this.sendMessage({
          type: 'join',
          playerId: this.playerId,
          displayName: this.scene.player?.name ?? 'Farmer',
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const data: ServerMessage = JSON.parse(event.data);
          this.handleMessage(data);
        } catch {
          // Malformed message, ignore
        }
      };

      this.ws.onclose = () => {
        this.connected = false;
        if (!this.intentionalDisconnect) {
          this.connectionState = 'reconnecting';
          this.scene.events.emit('connection-state', this.connectionState);
          this.scene.events.emit('notification', 'Disconnected - reconnecting...');
          this.scheduleReconnect();
        } else {
          this.connectionState = 'disconnected';
          this.scene.events.emit('connection-state', this.connectionState);
        }
      };

      this.ws.onerror = () => {
        this.connected = false;
        // onerror is always followed by onclose, so reconnect is handled there
      };
    } catch {
      this.connectionState = 'disconnected';
      this.scene.events.emit('connection-state', this.connectionState);
      this.scene.events.emit('notification', 'Multiplayer not available - playing offline');
    }
  }

  private scheduleReconnect(): void {
    if (this.intentionalDisconnect) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.connectionState = 'disconnected';
      this.scene.events.emit('connection-state', this.connectionState);
      this.scene.events.emit('notification', 'Could not reconnect. Playing offline.');
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.intentionalDisconnect) {
        this.scene.events.emit('notification', `Reconnecting... (attempt ${this.reconnectAttempts})`);
        this.openWebSocket();
      }
    }, delay);
  }

  private async ensureRoomExists(roomId: string): Promise<void> {
    const res = await fetch(`/api/rooms/${roomId}`);
    if (res.status === 404) {
      await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId,
          roomName: `Room ${roomId}`,
          hostPlayerId: this.playerId,
          maxPlayers: 4,
        }),
      });
    }
  }

  private handleMessage(data: ServerMessage): void {
    switch (data.type) {
      case 'room_state':
        if (data.room && data.room.players) {
          for (const [id, state] of Object.entries(data.room.players)) {
            if (id !== this.playerId) {
              this.scene.addOtherPlayer(id, state.x, state.y);
            }
          }
        }
        break;

      case 'init':
        this.playerId = data.playerId || this.playerId;
        if (data.players && !Array.isArray(data.players)) {
          for (const [id, state] of Object.entries(data.players)) {
            if (id !== this.playerId) {
              this.scene.addOtherPlayer(id, state.x, state.y);
            }
          }
        }
        break;

      case 'player_joined':
        if (data.player) {
          const p = data.player;
          if (p.playerId !== this.playerId) {
            this.scene.addOtherPlayer(p.playerId, p.x, p.y);
            const name = data.displayName ?? 'A player';
            this.scene.events.emit('notification', `${name} joined!`);
          }
        } else if (data.playerId && data.playerId !== this.playerId) {
          this.scene.addOtherPlayer(data.playerId, data.x ?? 800, data.y ?? 800);
          this.scene.events.emit('notification', 'A player joined!');
        }
        break;

      case 'player_moved':
        if (data.playerId && data.playerId !== this.playerId) {
          const dir = typeof data.direction === 'string'
            ? STRING_TO_DIR[data.direction] ?? Direction.DOWN
            : (data.direction as Direction) ?? Direction.DOWN;
          this.scene.updateOtherPlayer(data.playerId, data.x!, data.y!, dir);
        }
        break;

      case 'player_left':
        if (data.playerId && data.playerId !== this.playerId) {
          this.scene.removeOtherPlayer(data.playerId);
          this.scene.events.emit('notification', 'A player left');
        }
        break;

      case 'tick':
        // Sync other players' positions
        if (Array.isArray(data.players)) {
          const activePlayers = new Set<string>();
          for (const p of data.players as Array<{ playerId: string; x: number; y: number; direction: string; health?: number }>) {
            if (p.playerId !== this.playerId) {
              activePlayers.add(p.playerId);
              const dir = STRING_TO_DIR[p.direction] ?? Direction.DOWN;
              if (this.scene.otherPlayers.has(p.playerId)) {
                this.scene.updateOtherPlayer(p.playerId, p.x, p.y, dir);
              } else {
                this.scene.addOtherPlayer(p.playerId, p.x, p.y);
              }
            }
          }
          // Remove players not present in tick (they left between ticks)
          for (const [id] of this.scene.otherPlayers) {
            if (!activePlayers.has(id)) {
              this.scene.removeOtherPlayer(id);
            }
          }
        }

        // Sync day/night cycle from server
        if (data.dayCyclePhase && data.dayNumber !== undefined) {
          this.scene.events.emit('server-time-sync', {
            phase: data.dayCyclePhase,
            dayNumber: data.dayNumber,
            tick: data.dayCycleTick ?? 0,
          });
        }
        break;

      case 'day_cycle':
        if (data.phase && data.dayNumber !== undefined) {
          this.scene.events.emit('server-time-sync', {
            phase: data.phase,
            dayNumber: data.dayNumber,
            tick: 0,
          });
        }
        break;

      case 'chat':
        if (data.displayName && data.text) {
          this.scene.events.emit('notification', `[Chat] ${data.displayName}: ${data.text}`);
        }
        break;

      case 'attack_result':
        if (data.targetPlayerId === this.playerId) {
          this.scene.events.emit('notification', `You took ${data.damage} damage!`);
        }
        break;

      case 'player_defeated':
        if ((data as Record<string, unknown>).defeatedPlayerId === this.playerId) {
          this.scene.events.emit('notification', 'You were defeated! Respawning...');
        }
        break;

      case 'error':
        if (data.message) {
          this.scene.events.emit('notification', `Server: ${data.message}`);
        }
        break;
    }
  }

  update(time: number, _delta: number): void {
    if (!this.connected || !this.ws) return;

    if (time - this.lastSendTime < this.sendInterval) return;

    const player = this.scene.player;
    if (!player?.sprite) return;
    const x = player.sprite.x;
    const y = player.sprite.y;

    if (Math.abs(x - this.lastX) > 1 || Math.abs(y - this.lastY) > 1) {
      this.lastSendTime = time;
      this.lastX = x;
      this.lastY = y;

      this.sendMessage({
        type: 'move',
        x,
        y,
        direction: DIR_TO_STRING[player.direction] ?? 'down',
      });
    }
  }

  private sendMessage(msg: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(msg));
    } catch {
      // Socket closed between readyState check and send
    }
  }

  // --- Public API for syncing farm actions ---

  sendPlant(plantId: string, tileX: number, tileY: number): void {
    this.sendMessage({ type: 'plant', plantId, tileX, tileY });
  }

  sendWater(tileX: number, tileY: number): void {
    this.sendMessage({ type: 'water', tileX, tileY });
  }

  sendHarvest(tileX: number, tileY: number): void {
    this.sendMessage({ type: 'harvest', tileX, tileY });
  }

  sendAttack(targetPlayerId: string, plantId: string): void {
    this.sendMessage({ type: 'attack', targetPlayerId, plantId });
  }

  sendChat(text: string): void {
    this.sendMessage({ type: 'chat', text });
  }

  sendAction(actionType: string, data: Record<string, unknown>): void {
    this.sendMessage({ type: 'action', actionType, ...data });
  }

  disconnect(): void {
    this.intentionalDisconnect = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
    this.connectionState = 'disconnected';
    this.scene.events.emit('connection-state', this.connectionState);
  }

  isConnected(): boolean {
    return this.connected;
  }

  getPlayerId(): string {
    return this.playerId;
  }

  getRoomId(): string {
    return this.roomId;
  }
}
